import { useEffect, useRef, useCallback } from 'react';
import moment from 'moment-timezone';
import { devLog, menuLog, logger } from '../utils/logger';
import { useMenuStore } from '../store/menuState';
import { useInfoStore } from '../store/userInfo';
import { useOrderStore } from '../store/order';

// 전역 타이머 관리 (중복 방지)
let globalVisibilityTimer: NodeJS.Timeout | null = null;
let globalNestedTimer: NodeJS.Timeout | null = null; // 중첩된 타이머 추적
let isVisibilityTimerRunning = false;
let globalVisibilityTimerId = 0; // 타이머 ID로 중복 방지

// 전역 moment 객체 재사용 (메모리 최적화)
let globalMomentCache: moment.Moment | null = null;
let lastMomentUpdate = 0;
const MOMENT_CACHE_DURATION = 1000; // 1초마다 업데이트

// 캐시된 moment 객체 가져오기 (메모리 최적화)
function getCachedMoment(): moment.Moment {
  const now = Date.now();
  if (!globalMomentCache || (now - lastMomentUpdate) > MOMENT_CACHE_DURATION) {
    globalMomentCache = moment();
    lastMomentUpdate = now;
  }
  return globalMomentCache;
}

// 메뉴 경계 시간을 고려한 다음 체크 시간 계산 (즉시 처리 포함)
function msToNextMenuBoundary(): number {
  const now = getCachedMoment();
  const currentMinute = now.minutes();
  
  logger.log(`[useMenuVisibilityTick] Current time: ${now.format('HH:mm:ss')}, minute: ${currentMinute}`);
  
  // 현재 분이 5분 단위 경계에 있는지 확인
  const isAtBoundary = currentMinute % 5 === 0;
  
  if (isAtBoundary) {
    logger.log(`[useMenuVisibilityTick] At 5-minute boundary, will execute immediately`);
    return 1000; // 1초 후 즉시 실행
  }
  
  // 현재 분을 5분 단위로 올림하여 다음 체크 시간 계산
  const nextMinute = Math.ceil(currentMinute / 5) * 5;
  
  // 다음 5분 단위 시간 설정
  const nextFive = now.clone().minute(nextMinute).second(0).millisecond(0);
  
  // 다음 시간으로 넘어가는 경우 처리
  if (nextMinute >= 60) {
    nextFive.add(1, 'hour').minute(0);
  }
  
  const ms = nextFive.diff(now);
  
  logger.log(`[useMenuVisibilityTick] Next check at: ${nextFive.format('HH:mm:ss')}, waiting: ${Math.round(ms/1000)} seconds`);
  
  // 음수 값 방지 (최소 1초)
  return Math.max(ms, 1000);
}

// 실제 메뉴 영업시간 경계를 계산하여 정확한 체크 시간 반환
function msToNextActualMenuBoundary(): number {
  const now = getCachedMoment();
  const currentUserInfo = useInfoStore.getState().userInfo;
  const isAyce = currentUserInfo?.isAyce;
  
  logger.log(`[useMenuVisibilityTick] Calculating next menu boundary at ${now.format('HH:mm:ss')}`);
  
  // 모든 메뉴의 영업시간 경계를 수집
  const menuBoundaries = new Set<number>();
  
  if (isAyce) {
    // AYCE 메뉴의 영업시간 경계 수집
    const ayceMenus = useMenuStore.getState().ayceMenus;
    ayceMenus.forEach(menu => {
      if (menu.menuHours && menu.menuHours.length > 0) {
        const currentDay = now.day();
        const menuHour = menu.menuHours[currentDay];
        if (menuHour && !menuHour.isOpenAllDay && !menuHour.isClosed) {
          // 시작 시간과 종료 시간을 분 단위로 변환
          const startMinutes = menuHour.startHour * 60 + menuHour.startMinute;
          const endMinutes = menuHour.endHour * 60 + menuHour.endMinute;
          menuBoundaries.add(startMinutes);
          menuBoundaries.add(endMinutes);
        }
      }
    });
  } else {
    // 일반 메뉴의 영업시간 경계 수집
    const topMenus = useMenuStore.getState().topMenus;
    topMenus.forEach(menu => {
      if (menu.menuHours && menu.menuHours.length > 0) {
        const currentDay = now.day();
        const menuHour = menu.menuHours[currentDay];
        if (menuHour && !menuHour.isOpenAllDay && !menuHour.isClosed) {
          // 시작 시간과 종료 시간을 분 단위로 변환
          const startMinutes = menuHour.startHour * 60 + menuHour.startMinute;
          const endMinutes = menuHour.endHour * 60 + menuHour.endMinute;
          menuBoundaries.add(startMinutes);
          menuBoundaries.add(endMinutes);
        }
      }
    });
  }
  
  // 현재 시간을 분 단위로 변환
  const currentMinutes = now.hour() * 60 + now.minute();
  
  // 다음 경계 시간 찾기 (현재 시간과 정확히 일치하는 경계도 포함)
  let nextBoundary = null;
  for (const boundary of menuBoundaries) {
    if (boundary >= currentMinutes) {
      if (!nextBoundary || boundary < nextBoundary) {
        nextBoundary = boundary;
      }
    }
  }
  
  // 다음 경계가 없으면 내일 첫 번째 경계로 설정
  if (!nextBoundary && menuBoundaries.size > 0) {
    nextBoundary = Math.min(...menuBoundaries) + 24 * 60; // 내일 첫 번째 경계
  }
  
  if (nextBoundary) {
    // 현재 시간과 정확히 일치하는 경계면 즉시 실행
    if (nextBoundary === currentMinutes) {
      logger.log(`[useMenuVisibilityTick] At menu boundary ${Math.floor(nextBoundary/60)}:${(nextBoundary%60).toString().padStart(2,'0')}, executing immediately`);
      return 1000; // 1초 후 즉시 실행
    }
    
    const nextTime = now.clone().hour(Math.floor(nextBoundary / 60)).minute(nextBoundary % 60).second(0).millisecond(0);
    if (nextBoundary >= 24 * 60) {
      nextTime.add(1, 'day').hour(Math.floor((nextBoundary - 24 * 60) / 60)).minute((nextBoundary - 24 * 60) % 60);
    }
    
    const ms = nextTime.diff(now);
    logger.log(`[useMenuVisibilityTick] Next menu boundary at: ${nextTime.format('HH:mm:ss')}, waiting: ${Math.round(ms/1000)} seconds`);
    
    // 최소 1초, 최대 5분 간격으로 제한
    return Math.max(Math.min(ms, 5 * 60 * 1000), 1000);
  }
  
  // 경계가 없으면 5분 후에 다시 체크
  logger.log(`[useMenuVisibilityTick] No menu boundaries found, checking in 5 minutes`);
  return 5 * 60 * 1000;
}

export function useMenuVisibilityTick() {
  const { userInfo } = useInfoStore();
  const { selectedAyce } = useOrderStore();
  const { selectedMenu } = useMenuStore();
  const isOrderActive = useMenuStore((s) => s.selectedItem !== null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const currentVisibilityTimerId = useRef(0);

  // 타이머 정리 함수 (중첩된 타이머 포함)
  const cleanupVisibilityTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (globalVisibilityTimer) {
      clearTimeout(globalVisibilityTimer);
      globalVisibilityTimer = null;
    }
    if (globalNestedTimer) {
      clearTimeout(globalNestedTimer);
      globalNestedTimer = null;
    }
    isVisibilityTimerRunning = false;
    currentVisibilityTimerId.current = 0;
  }, []);

  // 메뉴 가시성 체크 함수 (주문 중에도 정상 업데이트)
  const checkMenuVisibility = useCallback(() => {
    logger.log(`[useMenuVisibilityTick] checkMenuVisibility called at ${moment().format('HH:mm:ss')}`);
    
    // AYCE/일반 모드 구분하여 처리
    const currentUserInfo = useInfoStore.getState().userInfo;
    const isAyce = currentUserInfo?.isAyce;
    
    logger.log(`[useMenuVisibilityTick] Current mode: ${isAyce ? 'AYCE' : 'Normal'}`);
    
    if (isAyce) {
      // AYCE 모드: AYCE 메뉴 가시성 업데이트 수행
      logger.log('[useMenuVisibilityTick] Updating AYCE menu visibility...');
      useMenuStore.getState().setVisibleAYCEMenus();
      logger.log('[useMenuVisibilityTick] AYCE menus visibility updated');
    } else {
      // 일반 모드: 주문 중이어도 메뉴 가시성 업데이트 수행
      logger.log('[useMenuVisibilityTick] Updating normal menu visibility...');
      useMenuStore.getState().setVisibleMenus();
      logger.log('[useMenuVisibilityTick] Normal menus visibility updated');
    }
  }, []); // 의존성 배열 제거로 메모리 누수 방지

  useEffect(() => {
    // 기존 타이머 정리 후 새로 시작
    cleanupVisibilityTimer();

    // 전역 타이머 시작
    globalVisibilityTimerId++;
    currentVisibilityTimerId.current = globalVisibilityTimerId;
    isVisibilityTimerRunning = true;

    // 첫 번째 실행 (즉시)
    checkMenuVisibility();

    // 5분 경계(00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55분)에 정확히 체크
    const startTimer = () => {
      const now = moment(); // 정확한 현재 시간 사용 (캐시 사용 안함)
      const currentMinute = now.minutes();
      
      // 현재 분이 5분 단위 경계에 있는지 확인
      const isAtBoundary = currentMinute % 5 === 0;
      
      if (isAtBoundary) {
        logger.log(`[useMenuVisibilityTick] At 5-minute boundary (${currentMinute}min), executing immediately`);
        checkMenuVisibility();
      }
      
      // 다음 5분 경계까지의 시간 계산
      const nextMinute = Math.ceil(currentMinute / 5) * 5;
      const nextFive = now.clone().minute(nextMinute).second(0).millisecond(0);
      
      // 다음 시간으로 넘어가는 경우 처리
      if (nextMinute >= 60) {
        nextFive.add(1, 'hour').minute(0);
      }
      
      const msToNext = nextFive.diff(now);
      logger.log(`[useMenuVisibilityTick] Next 5-minute boundary at: ${nextFive.format('HH:mm:ss')}, waiting: ${Math.round(msToNext/1000)} seconds`);

      globalVisibilityTimer = setTimeout(() => {
        // 타이머 ID 확인으로 중복 실행 방지
        if (currentVisibilityTimerId.current === globalVisibilityTimerId) {
          logger.log(`[useMenuVisibilityTick] 5-minute boundary timer executed at ${moment().format('HH:mm:ss')}`);
          checkMenuVisibility();
          startTimer(); // 재귀적으로 다음 타이머 설정
        }
      }, msToNext);
    };

    startTimer();

    return () => {
      cleanupVisibilityTimer();
    };
  }, []); // 의존성 배열 제거로 무한 루프 방지

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      cleanupVisibilityTimer();
    };
  }, [cleanupVisibilityTimer]);

  return null;
}
